import { Download as DownloadIcon } from "@mui/icons-material";
import { Box, Button, CircularProgress, Divider, Typography } from "@mui/material";
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiRequest } from "../utils/apiRequest";
import { PAGE_SIZES, usePersistedPagination } from "../utils/usePersistedPagination";
import { showToast } from "../utils/toastOptions";
import FilterMultiSelectWithSearch, { exactMulti } from "./FilterMultiSelectWithSearch";

const BASE = process.env.REACT_APP_API_BASE_URL;

const csvCell = (s) => {
  if (s == null) return "";
  s = String(s);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (filename, rows) => {
  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const USER_STATUS_OPTIONS = new Set(["Active", "Inactive"]);

function filterByStatus(row, _columnId, selected) {
  if (!Array.isArray(selected) || selected.length === 0) return true;
  const statusSelected = selected.filter((s) => USER_STATUS_OPTIONS.has(s));
  if (statusSelected.length === 0) return true;
  const status = row.original.is_active ? "Active" : "Inactive";
  return statusSelected.includes(status);
}

/**
 * Permission Audit tab — two modes:
 *
 * variant="company"  (companyId required)
 *   Calls GET /companies/{id}/permission-audit/ to walk every project and
 *   collect each user's permission template per project.
 *
 * variant="project"  (users prop required)
 *   Uses the already-loaded project users array (each user has
 *   permission_template and company_permission_template).
 */
export default function PermissionAuditTab({
  variant = "company",
  companyId,
  users: projectUsers,
  projectName,
  paginationKey,
}) {
  const [pagination, setPagination] = usePersistedPagination(paginationKey);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectCount, setProjectCount] = useState(null);

  const fetchAudit = useCallback(
    async (forceRefresh = false) => {
      if (variant !== "company" || !companyId) return;
      setLoading(true);
      try {
        const data = await apiRequest(
          `${BASE}/companies/${companyId}/permission-audit/`,
          { method: "GET", forceRefresh },
        );
        setRows(data?.rows || []);
        setProjectCount(data?.project_count ?? null);
      } catch (e) {
        showToast("error", e.message || "Failed to load permission audit");
      } finally {
        setLoading(false);
      }
    },
    [variant, companyId],
  );

  useEffect(() => {
    if (variant === "company") {
      fetchAudit();
    }
  }, [variant, fetchAudit]);

  useEffect(() => {
    if (variant === "project" && projectUsers) {
      setRows(
        (projectUsers || []).map((u) => ({
          user_id: u.id,
          user_name: u.name || "",
          email: u.email_address || "",
          permission_template: u.permission_template?.name || "",
          company_permission_template: u.company_permission_template?.name || "",
          is_active: u.is_active ?? true,
        })),
      );
    }
  }, [variant, projectUsers]);

  const columns = useMemo(() => {
    const cols = [
      {
        id: "user_name",
        accessorKey: "user_name",
        header: "User",
        minSize: 220,
        size: 240,
        muiTableHeadCellProps: {
          sx: {
            verticalAlign: "top",
            "& .MuiCollapse-wrapperInner": {
              display: "flex",
              alignItems: "flex-end",
              minHeight: 48,
              width: "100%",
            },
          },
        },
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterVariant: "multi-select",
        filterFn: filterByStatus,
        Filter: FilterMultiSelectWithSearch,
        meta: {
          getFilterTags: (row) => {
            return [row.original.is_active ? "Active" : "Inactive"];
          },
        },
        Cell: ({ row }) => {
          const u = row.original;
          const isActive = Boolean(u.is_active);
          return (
            <Box
              sx={{
                fontWeight: 500,
                color: isActive ? "success.main" : "error.main",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {u.user_name || "\u2014"}
            </Box>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        minSize: 300,
        size: 300,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() || "\u2014",
        enableGlobalFilter: false,
      },
    ];

    if (variant === "company") {
      cols.push({
        accessorKey: "project_name",
        header: "Project",
        minSize: 180,
        size: 180,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() || "\u2014",
        enableGlobalFilter: false,
      });
    }

    cols.push(
      {
        accessorKey: "permission_template",
        header: "Permission Template",
        minSize: 220,
        size: 220,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() || "\u2014",
        enableGlobalFilter: false,
      },
      {
        accessorKey: "company_permission_template",
        header: "Company Permission Template",
        minSize: 230,
        size: 230,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() || "\u2014",
        enableGlobalFilter: false,
      },
    );

    return cols;
  }, [variant]);

  const handleExport = useCallback(() => {
    const header =
      variant === "company"
        ? ["User", "Email", "Project", "Permission Template", "Company Permission Template", "Active"]
        : ["User", "Email", "Permission Template", "Company Permission Template", "Active"];

    const dataRows = rows.map((r) => {
      const base = [r.user_name, r.email];
      if (variant === "company") base.push(r.project_name);
      base.push(r.permission_template, r.company_permission_template, r.is_active ? "Yes" : "No");
      return base;
    });

    const stamp = new Date().toISOString().slice(0, 10);
    const prefix = variant === "company" ? `permission-audit-company-${companyId}` : `permission-audit-project`;
    downloadCsv(`${prefix}-${stamp}.csv`, [header, ...dataRows]);
  }, [rows, variant, companyId]);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    getRowId: (row, idx) =>
      variant === "company"
        ? `${row.user_id}-${row.project_id}-${idx}`
        : `${row.user_id}-${idx}`,

    enableColumnActions: false,
    enableSorting: false,
    enableRowSelection: false,
    enablePagination: true,
    enableColumnResizing: true,
    layoutMode: "grid",

    enableColumnFilters: true,
    enableFacetedValues: true,
    enableFilterMatchHighlighting: true,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    positionGlobalFilter: "left",
    muiSearchTextFieldProps: {
      placeholder: "Search users by name",
    },
    muiTableHeadCellProps: {
      sx: {
        "& .MuiCollapse-wrapperInner": {
          display: "flex",
          alignItems: "flex-end",
          minHeight: 48,
        },
      },
    },

    state: { pagination },
    onPaginationChange: setPagination,
    paginationDisplayMode: "pages",
    muiPaginationProps: { rowsPerPageOptions: PAGE_SIZES },
    muiBottomToolbarProps: {
      sx: {
        minHeight: "auto",
        "& > .MuiBox-root > .MuiBox-root": { position: "relative", top: "auto", right: "auto" },
        "& .MuiTablePagination-root": { py: 0 },
      },
    },

    initialState: {
      density: "compact",
      pagination: { pageSize: 10, pageIndex: 0 },
      showGlobalFilter: true,
      showColumnFilters: true,
      columnPinning: { left: ["user_name"] },
    },

    renderToolbarInternalActions: ({ table: t }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={rows.length === 0}
        >
          Export CSV
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Button
          variant="outlined"
          onClick={() => {
            t.resetColumnFilters();
            t.resetGlobalFilter();
          }}
        >
          Clear Filters
        </Button>
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={t} />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ShowHideColumnsButton table={t} />
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleDensePaddingButton table={t} />
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleFullScreenButton table={t} />
      </Box>
    ),

    positionToolbarAlertBanner: "none",
  });

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 1 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Loading permissions across all projects{projectCount != null ? ` (${projectCount})` : ""}{"\u2026"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {variant === "company" && projectCount != null && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {rows.length} assignment{rows.length !== 1 ? "s" : ""} across {projectCount} project
          {projectCount !== 1 ? "s" : ""}
          {projectName ? ` \u2014 ${projectName}` : ""}
        </Typography>
      )}
      {variant === "project" && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {rows.length} user{rows.length !== 1 ? "s" : ""} on this project
        </Typography>
      )}
      <Box sx={{ mt: 1 }}>
        <MaterialReactTable table={table} />
      </Box>
    </Box>
  );
}